/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import React, { useState } from "react";
import { Focus, Lesson, Topic } from "@ictlearningsciences/pal3-core/types";
import {
  GetRecommendedLessonsForTopic,
  GetRecommendedTopicsForFocus,
  GetValidLessons,
  OverrideGetRecommendedLessonsForTopic,
} from "@ictlearningsciences/pal3-core/recommender";

import { useAppSelector } from "../store/hooks";

export interface UseWithRecommender {
  recommendedLesson: Lesson | undefined;
  getRecommendedTopic: (focus: Focus) => Topic | undefined;
  getRecommendedTopics: (focus: Focus) => Topic[];
  asyncGetRecommendedLessons: (topic: Topic) => Promise<Lesson[]>;
}

export function useWithRecommender(): UseWithRecommender {
  const { topics } = useAppSelector((state) => state.data);
  const { user, records, activeGoal, activeTopic, surveyRecords } =
    useAppSelector((state) => state.userData);
  const [recommendedLesson, setRecommendedLesson] = useState<Lesson>();

  React.useEffect(() => {
    if (!activeTopic || records.length === 0) {
      return;
    }
    asyncGetRecommendedLessons(activeTopic).then((d) => {
      setRecommendedLesson(d[0]);
    });
  }, [records, topics, activeTopic]);

  function getRecommendedTopic(focus: Focus): Topic | undefined {
    const tp = getRecommendedTopics(focus);
    if (tp.length > 0) return tp[0];
  }

  function getRecommendedTopics(focus: Focus): Topic[] {
    return GetRecommendedTopicsForFocus(
      focus,
      records,
      surveyRecords?.variables
    );
  }

  function getRecommendedLessons(topic: Topic): Lesson[] {
    if (!activeGoal || !user) return [];
    if (topic.recommender === "linear_required") {
      return GetValidLessons(topic.lessons, user.traits).sort((a, b) => {
        if (a.ord < b.ord) return -1;
        if (a.ord > b.ord) return 1;
        return 0;
      });
    }
    return GetRecommendedLessonsForTopic(
      activeGoal,
      topic,
      records,
      user.traits
    );
  }

  async function asyncGetRecommendedLessons(topic: Topic): Promise<Lesson[]> {
    if (!activeGoal || !user) return [];
    if (activeGoal.recommenderOverride?.lessonRecUrl) {
      try {
        return OverrideGetRecommendedLessonsForTopic(
          activeGoal,
          topic,
          records,
          user
        );
      } catch (err) {
        console.error(err);
      }
    }
    return getRecommendedLessons(topic);
  }

  return {
    recommendedLesson,
    getRecommendedTopic,
    getRecommendedTopics,
    asyncGetRecommendedLessons,
  };
}
